<template>
  <div class="business-intelligence">
    <div class="header">
      <div class="insight">
        <span>{{ $t('usersTotalNumber') }}</span>
        <b>{{ formatNumber(clientCount) }}</b>
      </div>
      <div class="insight">
        <span>{{ $t('alertCount') }}</span>
        <b>{{ formatNumber(alertCount) }}</b>
      </div>
      <div class="insight">
        <span>{{ $t('monitoringsTotalNumber') }}</span>
        <b>{{ formatNumber(monitoringCount) }}</b>
      </div>
      <div class="search-bar">
        <input v-model="search" type="text" :placeholder="$t('searchByClientName')" class="input" />
        <IconSearch class="icon-search" />
      </div>
      <div class="access-input" @click="accountWithActivities = !accountWithActivities">
        <div class="checkbox" :class="{ checked: accountWithActivities }"></div>
        <div class="checkbox-label">{{ $t('actifUserAccounts') }}</div>
      </div>
    </div>
    <div class="main-button blue" @click="exportInsights">
      <div v-if="loadingExport" class="lds-ellipsis">
        <div></div>
        <div></div>
        <div></div>
        <div></div>
      </div>
      <IconImport v-else class="icon" />
      {{ !loadingExport ? $t('exportData') : '' }}
    </div>
    <table class="user-table">
      <thead>
        <tr>
          <td v-for="header in headers" :key="header.name">
            <div class="filter">
              <TableSort v-if="header.sort" v-model="sort" :filter="header.sort" />
              {{ header.name }}
            </div>
          </td>
          <td></td>
        </tr>
      </thead>
      <tbody v-if="loading || !clients.length">
        <tr>
          <td colspan="8">
            <div class="empty">
              <div v-if="loading" class="lds-ring-grey">
                <div></div>
                <div></div>
                <div></div>
                <div></div>
              </div>
              {{ loading ? $t('searchInProgress') : $t('noClient') }}
            </div>
          </td>
        </tr>
      </tbody>
      <tbody v-else>
        <tr v-for="client in clients" :key="`${client.clientId}${client.clientName}`" class="client">
          <td>
            {{ client.clientName }}
          </td>
          <td>{{ new Date(client.createdAt).toLocaleDateString('fr-FR') }}</td>
          <td>
            {{ client.userCount }}
            {{ $t('users', client.userCount > 1 ? 2 : 1) }}
          </td>
          <td>
            <div class="value">
              <span
                ><b>{{ formatNumber(client.monitoringCountCurrentYear) }}</b> en {{ currentYear }}</span
              >
              <span>{{ formatNumber(client.monitoringCountPreviousYear) }} en {{ pastYear }}</span>
            </div>
          </td>
          <td>
            <div class="value">
              <span
                ><b>{{ formatNumber(client.monitoringCountCurrentMonth) }}</b> en {{ currentMonth }}</span
              >
              <span>{{ formatNumber(client.monitoringCountPreviousMonth) }} en {{ pastMonth }}</span>
            </div>
          </td>
          <td>{{ formatPrice(client.amount) }}€</td>
          <td>
            {{ new Date(client.lastConnectionAt).toLocaleDateString('fr-FR') }}
          </td>
          <td>
            {{ new Date(client.lastMonitoringAt).toLocaleDateString('fr-FR') }}
          </td>
          <td>
            <RouterLink :to="{ name: 'ClientInsight', params: { id: client.clientId } }">{{ $t('activity') }}</RouterLink>
          </td>
        </tr>
      </tbody>
    </table>
    <Pagination
      v-if="clients && clients.length"
      :pagination="pagination"
      :set-page="setPage"
      :tableValues="clients"
      :pages="pages"
      :disable-dots="disableDots"
    />

    <LineChart :labels="labels" :alert-data="alertData" :monitoring-data="monitoringData" class="chart" />
    <PieChart :labels="pieLabels" :data="pieChartData" class="pie chart" />
  </div>
</template>

<script>
import LineChart from '@/components/LineChart';
import Pagination from '@/components/Pagination';
import PieChart from '@/components/PieChart';
import tableMixin from '@/components/tables/TableMixin';
import TableSort from '@/components/TableSort';
import debounce from '@/helpers/debounce';

export default {
  components: { TableSort, Pagination, PieChart, LineChart },
  mixins: [tableMixin],

  data() {
    return {
      headers: [
        {
          name: this.$t('clientName'),
          sort: null,
        },
        {
          name: this.$t('creationDate'),
          sort: 'createdAt',
        },
        {
          name: this.$t('usersNumber'),
          sort: 'userCount',
        },
        {
          name: `${this.$t('monitoringsNumber')} (${this.$t('year')})`,
          sort: 'monitoringCountCurrentYear',
        },
        {
          name: `${this.$t('monitoringsNumber')} (${this.$t('month')})`,
          sort: 'monitoringCountCurrentMonth',
        },
        {
          name: this.$t('totalSpent'),
          sort: 'amount',
        },
        {
          name: this.$t('lastConnection'),
          sort: 'lastConnectionAt',
        },
        {
          name: this.$t('lastMonitoring'),
          sort: 'lastMonitoringAt',
        },
      ],
      currentYear: 0,
      pastYear: 0,
      currentMonth: 0,
      pastMonth: 0,
      clients: [],
      sort: '-createdAt',
      search: '',
      accountWithActivities: true,
      tableMixinConfig: {
        backendPaginated: true,
        onChange: this.changePage,
        getData: this.getData,
        maxElement: 10,
      },
      debounce,
      searchDebounce: () => null,
      clientCount: 0,
      alertCount: 0,
      monitoringCount: 0,
      data: [],
      alertData: [],
      monitoringData: [],
      pieChartData: [],
      labels: [],
      pieLabels: [],
      loadingExport: false,
    };
  },

  computed: {
    lastPage() {
      return Math.ceil(this.clients.length / this.pageSize) - 1;
    },
  },

  watch: {
    search() {
      this.loading = true;
      this.searchDebounce();
    },

    sort() {
      this.getData();
    },

    accountWithActivities() {
      this.loading = true;
      this.getData();
    },
  },

  async created() {
    console.log('created');
    this.loading = true;
    const today = new Date();
    this.currentYear = today.getFullYear();
    this.pastYear = today.getFullYear() - 1;
    this.currentMonth = today.toLocaleString('fr-FR', { month: 'long' });
    if (today.getMonth() === 0) {
      this.pastMonth = new Date(this.pastYear, 11, today.getDate()).toLocaleString('fr-FR', { month: 'long' });
    } else {
      this.pastMonth = new Date(this.currentYear, today.getMonth() - 1, today.getDate()).toLocaleString('fr-FR', {
        month: 'long',
      });
    }
    this.searchDebounce = this.debounce(this.getData, 300);
    const insights = await this.$store.dispatch('getInsights');
    this.alertCount = insights.alertCount;
    this.clientCount = insights.clientCount;
    this.monitoringCount = insights.monitoringCount;
    this.data = await this.$store.dispatch('getMonitoringInsights');
    const packagesInsights = await this.$store.dispatch('getPackagesInsights');
    this.pieLabels = packagesInsights.map(insight => `${insight.key} (${insight.value})`);
    this.pieChartData = packagesInsights.map(insight => insight.value);
    this.groupDataByMonth(this.data);
  },

  methods: {
    groupDataByMonth(data) {
      let months = [];
      data.forEach(item => {
        const month = new Date(item.day).getMonth() + 1;
        const year = new Date(item.day).getFullYear();
        let currentMonth = months.find(item => item && item.month === month && item.year === year);
        if (!currentMonth) {
          months.push({
            month: month,
            year: year,
            day: this.formatChartMonthDate(item.day),
            monitoringData: item.monitoringCount,
            alertData: item.alertCount,
          });
        } else {
          currentMonth.monitoringData += item.monitoringCount;
          currentMonth.alertData += item.alertCount;
        }
      });
      months = months.sort((a, b) => new Date(a.year, a.month, 1) - new Date(b.year, b.month, 1));
      this.labels = months.map(m => m.day);
      this.monitoringData = months.map(m => m.monitoringData);
      this.alertData = months.map(m => m.alertData);
    },

    formatChartMonthDate(date) {
      let month = new Date(date).toLocaleDateString('fr-FR', { month: 'short' }).slice(0, 3).toUpperCase();
      let year = new Date(date).getFullYear();
      return `${month}. ${year}`;
    },

    formatNumber(n, toFixed = 0) {
      if (!Number.isNaN(n) && n != null) {
        return Number(n)
          .toFixed(toFixed)
          .replace(/./g, (c, i, a) => (i && c !== '\xa0' && (a.length - i) % 3 === 0 ? `\xa0${c}` : c))
          .replace('\xa0.', '.');
      }

      return 0;
    },

    formatPrice(price) {
      return this.formatNumber(price, 2);
    },

    async getData(pageNumber = 0, pageSize = this.tableMixinConfig.maxElement) {
      const result = await this.$store.dispatch('getClientsInsights', {
        pageNumber,
        pageSize,
        search: this.search,
        sortBy: this.sort,
        accountWithActivities: this.accountWithActivities,
      });
      this.clients = result.items;
      this.loading = false;
      this.pagination.totalLength = result.count;
      this.pagination.maxPage = Math.ceil(this.pagination.totalLength / this.pagination.maxElement);

      return { data: result, values: this.clients };
    },

    async exportInsights() {
      this.loadingExport = true;
      const result = await this.$store.dispatch('exportInsights', {
        search: this.search,
        accountWithActivities: this.accountWithActivities,
      });
      const content = result.headers['content-disposition'];
      const filename = content
        .split(';')
        .find(n => n.includes('filename='))
        .replace('filename=', '');
      const url = window.URL.createObjectURL(new Blob([result.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      const child = document.body.appendChild(link);
      link.click();
      child.remove();
      window.URL.revokeObjectURL(url);
      this.loadingExport = false;
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/responsive.scss';
@use '@/styles/fonts.scss';
.search-bar {
  display: flex;
  align-items: center;
  box-sizing: border-box;
  width: 40%;
  border: solid 1px colors.$lightgrey-color;
  background: colors.$white-color;
  height: 46px;
  padding-right: 16px;
  transition: 0.3s;

  .input {
    border: none;
    width: 100%;
    padding: 16px;
  }

  &:focus-within {
    border-color: colors.$lightgreen-color;
  }
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  margin: 40px 0 24px;
  width: 100%;

  .search-bar {
    margin-top: -40px;
  }

  .access-input {
    margin-left: auto;
    margin-top: -10px;
  }
}

.insight {
  display: flex;
  flex-direction: column;

  span {
    margin-bottom: 10px;
    font-weight: 600;
  }

  b {
    font-size: 28px;
    color: colors.$main-color;
  }
}

.user-table {
  border-collapse: collapse;
  width: 100%;

  td {
    width: calc(100% / 7);
    font-size: 12px;

    &:first-child {
      padding-left: 16px;
    }
  }
}

.value {
  display: flex;
  flex-direction: column;

  span {
    margin-bottom: 4px;

    &:last-child {
      font-size: 11px;
    }
  }
}

.access-input {
  display: flex;
  margin: 16px 0 8px 0;
  cursor: pointer;

  .checkbox-label {
    margin-left: 8px;
  }
}

.chart {
  width: 80%;
  margin: 0 auto;
  margin: 60px auto;

  &.pie {
    width: 60%;
  }
}

.empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 16px;
  width: 100%;
  margin: 0 auto;

  .lds-ring-grey {
    margin-right: 30px;
  }
}

thead {
  background: colors.$main-color;
  border: 1px solid colors.$main-color;

  td {
    height: 48px;
    font-weight: 500;
    color: colors.$white-color;
  }
}

.filter {
  display: flex;
  align-items: center;
}

tbody {
  border: 1px solid colors.$lightgrey-color;

  td {
    padding: 6px 20px;

    &:first-child {
      padding-left: 16px;
    }

    a {
      text-decoration: underline;
      color: colors.$main-color;
      transition: 0.3s;
      cursor: pointer;

      &:hover {
        color: colors.$darkblue-color;
      }
    }
  }

  tr {
    border-bottom: 1px solid colors.$lightgrey-color;
  }
}

.main-button {
  &.blue {
    width: max-content;
    background: colors.$lightblue-color;
    border-radius: 5px;
    font-size: 14px;
    font-weight: 500;
    padding: 8px 12px;
    margin: 0 0 24px auto;

    .icon {
      margin-right: 8px;
    }
  }
}
</style>
